import { MemberInsights } from '@frontend/gql/social/graphql';
import { ColumnDefinitionInput, RowType } from '..';
import { generateSubColumnConfig } from './generateSubColumnConfig';

export const MEMBER_TABLE_COLUMN_CONFIG_VERSION = 11; // must update this value if you change anything below
const baseDefaultColumnIds = [
  'member',
  'socialAccounts',
  'segment',
  'total.numberOfPosts',
  'total.activePosts',
  'total.latestPostDate',
  'total.engagements',
  'total.engagementRate',
  'total.impressions',
  'total.potentialReach',
  'total.tmv',
];

// Helper function to add/remove paid and organic metrics from any column array
export const adjustColumnsForPaidMetrics = (columns: string[], includePaidMetrics: boolean): string[] => {
  const paidMetricsColumns = ['total.paidEngagements', 'total.paidImpressions'];
  const organicMetricsColumns = ['total.organicEngagements', 'total.organicImpressions'];

  if (includePaidMetrics) {
    // Add organic and paid metrics if not already present
    const result = [...columns];

    // Add organic engagements after engagements if not present
    if (!result.includes('total.organicEngagements')) {
      const engagementsIndex = result.indexOf('total.engagements');
      if (engagementsIndex !== -1) {
        result.splice(engagementsIndex + 1, 0, 'total.organicEngagements');
      }
    }

    // Add paid engagements after organic engagements (or after engagements if organic not present)
    if (!result.includes('total.paidEngagements')) {
      const organicEngagementsIndex = result.indexOf('total.organicEngagements');
      const engagementsIndex = result.indexOf('total.engagements');
      const insertIndex = organicEngagementsIndex !== -1 ? organicEngagementsIndex + 1 : engagementsIndex + 1;
      if (insertIndex > 0) {
        result.splice(insertIndex, 0, 'total.paidEngagements');
      }
    }

    // Add organic impressions after impressions if not present
    if (!result.includes('total.organicImpressions')) {
      const impressionsIndex = result.indexOf('total.impressions');
      if (impressionsIndex !== -1) {
        result.splice(impressionsIndex + 1, 0, 'total.organicImpressions');
      }
    }

    // Add paid impressions after organic impressions (or after impressions if organic not present)
    if (!result.includes('total.paidImpressions')) {
      const organicImpressionsIndex = result.indexOf('total.organicImpressions');
      const impressionsIndex = result.indexOf('total.impressions');
      const insertIndex = organicImpressionsIndex !== -1 ? organicImpressionsIndex + 1 : impressionsIndex + 1;
      if (insertIndex > 0) {
        result.splice(insertIndex, 0, 'total.paidImpressions');
      }
    }

    return result;
  } else {
    // Remove paid and organic metrics columns if present
    return columns.filter((col) => !paidMetricsColumns.includes(col) && !organicMetricsColumns.includes(col));
  }
};

export const getDefaultVisibleMemberColumnIds = (includePaidMetrics: boolean = false): string[] => {
  return adjustColumnsForPaidMetrics(baseDefaultColumnIds, includePaidMetrics);
};

// Helper function to get columns with correct visibility based on feature flag
export const getColumnsWithFeatureFlagVisibility = (includePaidMetrics: boolean): ColumnDefinitionInput[] => {
  const visibleColumnIds = new Set(getDefaultVisibleMemberColumnIds(includePaidMetrics));

  return defaultColumns.map((column) => {
    const isVisible = visibleColumnIds.has(column.id);

    // For organic and paid metrics columns, regenerate subcolumns with correct visibility
    if (column.id.includes('organic') || column.id.includes('paid')) {
      const fieldName = column.id.replace('total.', '');
      return {
        ...column,
        hidden: !isVisible,
        subColumns: generateSubColumnConfig(fieldName, column.rowType, undefined, !isVisible),
      };
    }

    return {
      ...column,
      hidden: !isVisible,
    };
  });
};

// Helper function to merge saved user preferences with current default configuration
export const mergeColumnConfigWithDefaults = (
  savedColumns: ColumnDefinitionInput[],
  includePaidMetrics: boolean,
): ColumnDefinitionInput[] => {
  const defaultCols = getColumnsWithFeatureFlagVisibility(includePaidMetrics);
  
  // Create a recursive map for all saved columns (including nested subcolumns)
  const savedColumnsMap = new Map<string, any>();
  
  const buildColumnsMap = (columns: any[]) => {
    columns.forEach(col => {
      savedColumnsMap.set(col.id, col);
      if (col.subColumns && Array.isArray(col.subColumns)) {
        buildColumnsMap(col.subColumns);
      }
    });
  };
  
  buildColumnsMap(savedColumns);

  const mergeColumn = (defaultCol: ColumnDefinitionInput): ColumnDefinitionInput => {
    const savedCol = savedColumnsMap.get(defaultCol.id);

    // Recursively merge subColumns if they exist
    let mergedSubColumns = defaultCol.subColumns;
    if (defaultCol.subColumns && Array.isArray(defaultCol.subColumns)) {
      mergedSubColumns = defaultCol.subColumns.map((defaultSubCol: any) => mergeColumn(defaultSubCol));
    }

    if (!savedCol) {
      // If no saved config for this column, use default with merged subcolumns
      return {
        ...defaultCol,
        subColumns: mergedSubColumns
      };
    }

    // Merge column: preserve user customizations, use current defaults for system properties
    return {
      ...defaultCol, // Start with current defaults (styling, functions, etc.)
      // Override with user-customizable properties from saved config
      hidden: savedCol.hidden ?? defaultCol.hidden,
      order: savedCol.order ?? defaultCol.order,
      subColumns: mergedSubColumns,
      // Note: borderColor, rowType, header, enableSorting, accessorFn come from defaultCol
    };
  };

  return defaultCols.map(mergeColumn);
};

const defaultColumnsSet = new Set(getDefaultVisibleMemberColumnIds(false)); // Default to no paid metrics

export const defaultColumns: ColumnDefinitionInput[] = [
  {
    header: 'Member',
    id: 'member',
    enableSorting: true,
    enableGlobalFiltering: true,
    rowType: RowType.MEMBER,
    hidden: !defaultColumnsSet.has('member'),
    size: 200,
    minSize: 50,
    maxSize: 300,
    order: 1,
    isFixed: true,
    accessorFn: (row: MemberInsights) => row.member?.name ?? '',
  },
  {
    header: 'Social Accounts',
    id: 'socialAccounts',
    enableSorting: false,
    borderColor: '#A2ADB9',
    rowType: RowType.SOCIAL_HANDLES,
    minSize: 225,
    hidden: !defaultColumnsSet.has('socialAccounts'),
    order: 2,
    subColumns: [
      {
        header: 'Instagram',
        id: 'member.instagramUsername',
        enableSorting: true,
        enableGlobalFiltering: true,
        rowType: RowType.DEFAULT,
        hidden: !defaultColumnsSet.has('socialAccounts'),
      },
      {
        header: 'YouTube',
        id: 'member.youtubeUsername',
        enableSorting: true,
        enableGlobalFiltering: true,
        rowType: RowType.DEFAULT,
        hidden: !defaultColumnsSet.has('socialAccounts'),
      },
      {
        header: 'Pinterest',
        id: 'member.pinterestUsername',
        enableSorting: true,
        enableGlobalFiltering: true,
        rowType: RowType.DEFAULT,
        hidden: !defaultColumnsSet.has('socialAccounts'),
      },
      {
        header: 'Tiktok',
        id: 'member.tiktokUsername',
        enableSorting: true,
        enableGlobalFiltering: true,
        rowType: RowType.DEFAULT,
        hidden: !defaultColumnsSet.has('socialAccounts'),
      },
    ],
  },
  {
    header: 'Member Tier',
    id: 'member.segment',
    enableSorting: true,
    enableGlobalFiltering: true,
    rowType: RowType.SEGMENT,
    hidden: !defaultColumnsSet.has('segment'),
    size: 160,
    minSize: 140,
    maxSize: 200,
    order: 3,
    accessorFn: (row: MemberInsights) => row.member?.segment ?? '',
  },
  {
    header: 'Posts Created',
    id: 'total.numberOfPosts',
    enableSorting: true,
    rowType: RowType.NUMBER,
    borderColor: 'hsl(var(--refresh-blue-4))',
    hidden: !defaultColumnsSet.has('total.numberOfPosts'),
    order: 4,
    minSize: 185,
    subColumns: generateSubColumnConfig(
      'numberOfPosts',
      RowType.NUMBER,
      undefined,
      !defaultColumnsSet.has('total.numberOfPosts'),
    ),
  },
  {
    header: 'Active Posts',
    id: 'total.activePosts',
    enableSorting: true,
    rowType: RowType.NUMBER,
    borderColor: 'hsl(var(--refresh-blue-3))',
    hidden: !defaultColumnsSet.has('total.activePosts'),
    order: 5,
    minSize: 180,
    subColumns: generateSubColumnConfig(
      'activePosts',
      RowType.NUMBER,
      undefined,
      !defaultColumnsSet.has('total.activePosts'),
    ),
  },
  {
    header: 'Last Post Date',
    id: 'total.latestPostDate',
    enableSorting: true,
    rowType: RowType.DATE,
    hidden: !defaultColumnsSet.has('total.latestPostDate'),
    order: 6,
    minSize: 190,
    borderColor: 'hsl(var(--refresh-blue-4))',
    subColumns: generateSubColumnConfig(
      'latestPostDate',
      RowType.DATE,
      undefined,
      !defaultColumnsSet.has('total.latestPostDate'),
    ),
  },
  {
    header: 'Last Update Date',
    id: 'total.latestUpdateDate',
    enableSorting: true,
    rowType: RowType.DATE,
    hidden: !defaultColumnsSet.has('total.latestUpdateDate'),
    order: 7,
    minSize: 210,
    borderColor: 'hsl(var(--refresh-blue-3))',
    subColumns: generateSubColumnConfig(
      'latestUpdateDate',
      RowType.DATE,
      undefined,
      !defaultColumnsSet.has('total.latestUpdateDate'),
    ),
  },
  {
    header: 'Engagement',
    id: 'total.engagements',
    enableSorting: true,
    hidden: !defaultColumnsSet.has('total.engagements'),
    rowType: RowType.NUMBER,
    borderColor: 'hsl(var(--refresh-pink-4))',
    order: 8,
    subColumns: generateSubColumnConfig(
      'engagements',
      RowType.NUMBER,
      undefined,
      !defaultColumnsSet.has('total.engagements'),
    ),
  },
  {
    header: 'Organic Engagements',
    id: 'total.organicEngagements',
    enableSorting: true,
    hidden: !defaultColumnsSet.has('total.organicEngagements'),
    rowType: RowType.NUMBER,
    borderColor: 'hsl(var(--refresh-pink-3))',
    order: 8.3,
    subColumns: generateSubColumnConfig(
      'organicEngagements',
      RowType.NUMBER,
      undefined,
      !defaultColumnsSet.has('total.organicEngagements'),
    ),
  },
  {
    header: 'Paid Engagements',
    id: 'total.paidEngagements',
    enableSorting: true,
    hidden: !defaultColumnsSet.has('total.paidEngagements'),
    rowType: RowType.NUMBER,
    borderColor: 'hsl(var(--refresh-pink-3))',
    order: 8.5,
    subColumns: generateSubColumnConfig(
      'paidEngagements',
      RowType.NUMBER,
      undefined,
      !defaultColumnsSet.has('total.paidEngagements'),
    ),
  },
  {
    header: 'Engagement Rate',
    id: 'total.engagementRate',
    enableSorting: true,
    rowType: RowType.PERCENTAGE,
    hidden: !defaultColumnsSet.has('total.engagementRate'),
    borderColor: 'hsl(var(--refresh-pink-2))',
    order: 9,
    minSize: 190,
    subColumns: generateSubColumnConfig(
      'engagementRate',
      RowType.PERCENTAGE,
      undefined,
      !defaultColumnsSet.has('total.engagementRate'),
    ),
  },
  {
    header: 'Likes',
    id: 'total.likes',
    enableSorting: true,
    rowType: RowType.NUMBER,
    hidden: !defaultColumnsSet.has('total.likes'),
    borderColor: 'hsl(var(--refresh-orange-3))',
    order: 10,
    subColumns: generateSubColumnConfig('likes', RowType.NUMBER, ['Pinterest'], !defaultColumnsSet.has('total.likes')),
  },
  {
    header: 'Comments',
    id: 'total.comments',
    enableSorting: true,
    rowType: RowType.NUMBER,
    hidden: !defaultColumnsSet.has('total.comments'),
    borderColor: 'hsl(var(--refresh-teal-3))',
    order: 11,
    subColumns: generateSubColumnConfig(
      'comments',
      RowType.NUMBER,
      undefined,
      !defaultColumnsSet.has('total.comments'),
    ),
  },
  {
    header: 'Saves',
    id: 'total.saves',
    enableSorting: true,
    rowType: RowType.NUMBER,
    hidden: !defaultColumnsSet.has('total.saves'),
    borderColor: 'hsl(var(--refresh-lime-3))',
    order: 12,
    subColumns: generateSubColumnConfig('saves', RowType.NUMBER, ['Pinterest'], !defaultColumnsSet.has('total.saves')),
  },
  {
    header: 'Shares',
    id: 'total.shares',
    enableSorting: true,
    rowType: RowType.NUMBER,
    hidden: !defaultColumnsSet.has('total.shares'),
    borderColor: 'hsl(var(--refresh-purple-2))',
    order: 13,
    subColumns: generateSubColumnConfig(
      'shares',
      RowType.NUMBER,
      ['Pinterest', 'YouTube'],
      !defaultColumnsSet.has('total.shares'),
    ),
  },
  {
    header: 'Impressions',
    id: 'total.impressions',
    enableSorting: true,
    rowType: RowType.NUMBER,
    hidden: !defaultColumnsSet.has('total.impressions'),
    borderColor: 'hsl(var(--refresh-magenta-4))',
    order: 14,
    subColumns: generateSubColumnConfig(
      'impressions',
      RowType.NUMBER,
      undefined,
      !defaultColumnsSet.has('total.impressions'),
    ),
  },
  {
    header: 'Organic Impressions',
    id: 'total.organicImpressions',
    enableSorting: true,
    rowType: RowType.NUMBER,
    hidden: !defaultColumnsSet.has('total.organicImpressions'),
    borderColor: 'hsl(var(--refresh-magenta-3))',
    order: 14.3,
    subColumns: generateSubColumnConfig(
      'organicImpressions',
      RowType.NUMBER,
      undefined,
      !defaultColumnsSet.has('total.organicImpressions'),
    ),
  },
  {
    header: 'Paid Impressions',
    id: 'total.paidImpressions',
    enableSorting: true,
    rowType: RowType.NUMBER,
    hidden: !defaultColumnsSet.has('total.paidImpressions'),
    borderColor: 'hsl(var(--refresh-magenta-3))',
    order: 14.5,
    subColumns: generateSubColumnConfig(
      'paidImpressions',
      RowType.NUMBER,
      undefined,
      !defaultColumnsSet.has('total.paidImpressions'),
    ),
  },
  {
    header: 'Total Reach',
    id: 'total.reach',
    enableSorting: true,
    rowType: RowType.NUMBER,
    hidden: !defaultColumnsSet.has('total.reach'),
    borderColor: 'hsl(var(--refresh-blue-5))',
    order: 15,
    subColumns: generateSubColumnConfig(
      'reach',
      RowType.NUMBER,
      ['Pinterest', 'YouTube'],
      !defaultColumnsSet.has('total.reach'),
    ),
  },
  {
    header: 'Potential Reach',
    id: 'total.potentialReach',
    enableSorting: true,
    rowType: RowType.NUMBER,
    hidden: !defaultColumnsSet.has('total.potentialReach'),
    borderColor: 'hsl(var(--refresh-blue-5))',
    order: 16,
    minSize: 195,
    subColumns: generateSubColumnConfig(
      'potentialReach',
      RowType.NUMBER,
      undefined,
      !defaultColumnsSet.has('total.potentialReach'),
    ),
  },

  {
    header: 'Views',
    id: 'total.views',
    enableSorting: true,
    rowType: RowType.NUMBER,
    hidden: !defaultColumnsSet.has('total.views'),
    borderColor: 'hsl(var(--refresh-yellow-4))',
    order: 17,
    subColumns: generateSubColumnConfig('views', RowType.NUMBER, undefined, !defaultColumnsSet.has('total.views')),
  },
  {
    header: 'Total View Time',
    id: 'total.viewTimeTotalSeconds',
    enableSorting: true,
    rowType: RowType.DURATION,
    hidden: !defaultColumnsSet.has('total.viewTimeTotalSeconds'),
    borderColor: 'hsl(var(--refresh-blue-3))',
    order: 18,
    subColumns: generateSubColumnConfig(
      'viewTimeTotalSeconds',
      RowType.DURATION,
      undefined,
      !defaultColumnsSet.has('total.viewTimeTotalSeconds'),
    ),
  },
  {
    header: 'Avg View Time',
    id: 'total.viewTimeAvgSeconds',
    enableSorting: true,
    rowType: RowType.DURATION,
    hidden: !defaultColumnsSet.has('total.viewTimeAvgSeconds'),
    borderColor: 'hsl(var(--refresh-purple-3))',
    order: 19,
    subColumns: generateSubColumnConfig(
      'viewTimeAvgSeconds',
      RowType.DURATION,
      undefined,
      !defaultColumnsSet.has('total.viewTimeAvgSeconds'),
    ),
  },
  {
    header: 'TMV',
    id: 'total.tmv',
    enableSorting: true,
    rowType: RowType.CURRENCY,
    hidden: !defaultColumnsSet.has('total.tmv'),
    borderColor: 'hsl(var(--refresh-green-4))',
    order: 20,
    subColumns: generateSubColumnConfig('tmv', RowType.CURRENCY, undefined, !defaultColumnsSet.has('total.tmv')),
  },
  {
    header: 'Follower Growth',
    id: 'total.followerGrowth',
    enableSorting: true,
    rowType: RowType.NUMBER,
    hidden: !defaultColumnsSet.has('total.followerGrowth'),
    borderColor: 'hsl(var(--refresh-magenta-2))',
    order: 21,
    minSize: 200,
    subColumns: generateSubColumnConfig(
      'followerGrowth',
      RowType.NUMBER,
      ['Pinterest', 'Tiktok', 'YouTube'],
      !defaultColumnsSet.has('total.followerGrowth'),
    ),
  },
];
